import { Link } from "react-router-dom";
import { Clock, Mail, MapPin, Phone, Smartphone } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { SERVICES } from "@/data/services";
import { KOELN, UMLAND } from "@/data/areas";
import { SITE } from "@/lib/site";

const linkCls = "text-[15px] text-white/75 transition-colors hover:text-brand-yellow";
const headCls = "mb-4 text-xs font-bold uppercase tracking-[0.14em] text-brand-yellow";

export const Footer = () => (
  <footer className="bg-brand-anthracite text-white" data-testid="site-footer">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr] lg:px-8">
      <div className="space-y-5">
        <Logo light testId="footer-logo" />
        <address className="space-y-2.5 not-italic text-[15px] text-white/80">
          <p className="font-semibold text-white">{SITE.name}</p>
          <p className="flex gap-2.5"><MapPin className="mt-1 h-4 w-4 shrink-0 text-brand-yellow" />{SITE.street}<br />{SITE.zip} {SITE.city}-{SITE.district}</p>
          <a href={SITE.phoneHref} className="flex items-center gap-2.5 hover:text-brand-yellow" data-testid="footer-phone"><Phone className="h-4 w-4 text-brand-yellow" />{SITE.phoneIntl}</a>
          <a href={SITE.mobileHref} className="flex items-center gap-2.5 hover:text-brand-yellow" data-testid="footer-mobile"><Smartphone className="h-4 w-4 text-brand-yellow" />{SITE.mobileIntl}</a>
          <a href={`mailto:${SITE.email}`} className="flex items-center gap-2.5 break-all hover:text-brand-yellow" data-testid="footer-email"><Mail className="h-4 w-4 shrink-0 text-brand-yellow" />{SITE.email}</a>
        </address>
      </div>
      <div>
        <p className={headCls}>Leistungen</p>
        <ul className="space-y-2.5">
          {SERVICES.map((s) => (
            <li key={s.slug}><Link to={`/${s.slug}`} className={linkCls} data-testid={`footer-service-${s.slug}`}>{s.name}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <p className={headCls}>Service</p>
        <ul className="space-y-2.5">
          <li><Link to="/schaden-melden" className={linkCls} data-testid="footer-damage-link">Schaden melden</Link></li>
          <li><Link to="/faq" className={linkCls} data-testid="footer-faq-link">FAQ</Link></li>
          <li><Link to="/kontakt" className={linkCls} data-testid="footer-contact-link">Kontakt & Anfahrt</Link></li>
          <li><Link to="/impressum" className={linkCls} data-testid="footer-impressum-link">Impressum</Link></li>
          <li><Link to="/datenschutz" className={linkCls} data-testid="footer-privacy-link">Datenschutz</Link></li>
        </ul>
      </div>
      <div>
        <p className={headCls}>Erreichbarkeit</p>
        <ul className="space-y-3 text-[15px] text-white/80">
          {SITE.hours.map((h) => (
            <li key={h.days} className="flex gap-2.5"><Clock className="mt-1 h-4 w-4 text-brand-yellow" /><span><span className="font-semibold text-white">{h.days}</span><br />{h.time}</span></li>
          ))}
        </ul>
        <p className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/75">{SITE.partner}<br />Einsatz in Köln & Umgebung</p>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="footer-areas">
        <p className={headCls}>
          <Link to="/einsatzgebiet" className="hover:underline" data-testid="footer-areas-overview">Einsatzgebiet</Link>
        </p>
        <div className="grid gap-6 md:grid-cols-[2fr_1fr]">
          {[{ title: "Köln", items: KOELN }, { title: "Umland", items: UMLAND }].map((g) => (
            <div key={g.title}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/45">{g.title}</p>
              <ul className="flex flex-wrap gap-x-5 gap-y-2">
                {g.items.map((l) => (
                  <li key={l.slug}><Link to={`/${l.slug}`} className="text-sm text-white/75 transition-colors hover:text-brand-yellow" data-testid={`footer-area-${l.slug}`}>Kfz-Gutachter {l.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-6 text-xs text-white/55 sm:flex-row sm:justify-between sm:px-6 lg:px-8">
        <p>© {new Date().getFullYear()} {SITE.name}</p>
        <nav className="flex gap-5" aria-label="Rechtliches">
          <Link to="/impressum" className="hover:text-brand-yellow" data-testid="footer-bottom-impressum">Impressum</Link>
          <Link to="/datenschutz" className="hover:text-brand-yellow" data-testid="footer-bottom-privacy">Datenschutz</Link>
        </nav>
      </div>
    </div>
  </footer>
);
